/**
 * Notification System - Consistent toast notifications across the app
 * 
 * Provides a unified API for showing success, error, loading states
 * and promise-based notifications with automatic state management.
 * 
 * @example
 * ```typescript
 * // Simple notifications
 * notify.success('Salvo com sucesso!');
 * notify.error('Erro ao salvar', 'Tente novamente');
 * 
 * // Loading state
 * const toastId = notify.loading('Salvando...');
 * // ... later
 * toast.dismiss(toastId);
 * 
 * // Promise-based (auto handles loading/success/error)
 * notify.promise(
 *   saveData(),
 *   {
 *     loading: 'Salvando...',
 *     success: 'Salvo com sucesso!',
 *     error: (err) => `Erro: ${err.message}`
 *   }
 * );
 * ```
 */

import { toast } from 'sonner';

/**
 * Notification helper with consistent messaging
 */
export const notify = {
  /**
   * Show success notification
   */
  success: (message: string, description?: string) => {
    return toast.success(message, {
      description,
      duration: 4000,
    });
  },

  /**
   * Show error notification
   */
  error: (message: string, description?: string) => {
    return toast.error(message, {
      description,
      duration: 5000,
    });
  },

  /**
   * Show warning notification
   */
  warning: (message: string, description?: string) => {
    return toast.warning(message, {
      description,
      duration: 4000,
    });
  },

  /**
   * Show info notification
   */
  info: (message: string, description?: string) => {
    return toast.info(message, {
      description,
      duration: 4000,
    });
  },

  /**
   * Show loading notification (returns ID for later dismissal)
   */
  loading: (message: string) => {
    return toast.loading(message);
  },

  /**
   * Dismiss specific notification
   */
  dismiss: (toastId: string | number) => {
    toast.dismiss(toastId);
  },

  /**
   * Dismiss all notifications
   */
  dismissAll: () => {
    toast.dismiss();
  },

  /**
   * Promise-based notification with automatic state management
   * Handles loading -> success/error automatically
   */
  promise: <T,>(
    promise: Promise<T>,
    messages: {
      loading: string;
      success: string | ((data: T) => string);
      error: string | ((error: Error) => string);
    }
  ) => {
    return toast.promise(promise, messages);
  },
};

/**
 * Pre-configured notifications for common operations
 */
export const commonNotifications = {
  // Create operations
  created: (entity: string) => 
    notify.success(`${entity} criado com sucesso!`),
  
  // Update operations
  updated: (entity: string) => 
    notify.success(`${entity} atualizado com sucesso!`),
  
  // Delete operations
  deleted: (entity: string) => 
    notify.success(`${entity} excluído com sucesso!`),
  
  // Save operations (generic)
  saved: () => 
    notify.success('Alterações salvas!'),
  
  // Copy operations
  copied: () => 
    notify.success('Copiado para área de transferência'),
  
  // Network errors
  networkError: () => 
    notify.error('Erro de conexão', 'Verifique sua internet e tente novamente'),
  
  // Permission errors
  permissionError: () => 
    notify.error('Sem permissão', 'Você não tem permissão para essa ação'),
  
  // Validation errors
  validationError: (message?: string) => 
    notify.error('Dados inválidos', message || 'Verifique os campos e tente novamente'),
  
  // Generic error
  genericError: (message?: string) => 
    notify.error('Erro', message || 'Algo deu errado. Tente novamente'),
  
  // Coming soon
  comingSoon: (feature?: string) => 
    notify.info('Em breve', feature ? `${feature} será implementado em breve` : 'Funcionalidade em desenvolvimento'),
};

/**
 * Notification for async operations (mutations)
 */
export function notifyMutation<TData, TError extends Error>(
  promise: Promise<TData>,
  options: {
    entity: string;
    operation: 'create' | 'update' | 'delete';
    onSuccess?: (data: TData) => void;
    onError?: (error: TError) => void;
  }
) {
  const messages = {
    create: {
      loading: `Criando ${options.entity}...`,
      success: `${options.entity} criado com sucesso!`,
      error: (err: Error) => `Erro ao criar ${options.entity}: ${err.message}`,
    },
    update: {
      loading: `Atualizando ${options.entity}...`,
      success: `${options.entity} atualizado com sucesso!`,
      error: (err: Error) => `Erro ao atualizar ${options.entity}: ${err.message}`,
    },
    delete: {
      loading: `Excluindo ${options.entity}...`,
      success: `${options.entity} excluído com sucesso!`,
      error: (err: Error) => `Erro ao excluir ${options.entity}: ${err.message}`,
    },
  };

  return toast.promise(
    promise.then((data) => {
      options.onSuccess?.(data);
      return data;
    }).catch((error) => {
      options.onError?.(error);
      throw error;
    }),
    messages[options.operation]
  );
}

/**
 * Batch notification for multiple operations
 */
export function notifyBatch(
  promises: Promise<any>[],
  options: {
    loading: string;
    success: string;
    error: string;
  }
) {
  return toast.promise(
    Promise.all(promises),
    options
  );
}
